import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, CheckCircle, XCircle, Phone, Mail,
  FileText, Image, ExternalLink, Clock, PauseCircle, Eye, X, Upload, Plus, Trash2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { useAdminAuth } from '../context/AdminAuthContext';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/utils';

const RegistrationDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { api } = useAdminAuth();

  const [registration, setRegistration] = useState(null);
  const [loading, setLoading] = useState(true);
  const [validationStatus, setValidationStatus] = useState({});
  const [invalidReasons, setInvalidReasons] = useState({});
  const [validationNotes, setValidationNotes] = useState('');
  const [requestedFiles, setRequestedFiles] = useState([{ label: '' }]); // Files admin wants the applicant to upload
  const [clarifications, setClarifications] = useState([]);
  const [selectedClarification, setSelectedClarification] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showDocumentModal, setShowDocumentModal] = useState(false);

  // On Hold Dialog
  const [showOnHoldDialog, setShowOnHoldDialog] = useState(false);
  const [onHoldReason, setOnHoldReason] = useState('');
  const [additionalInfoNeeded, setAdditionalInfoNeeded] = useState('');

  const normalizedStatus = registration?.status?.trim().toLowerCase();
  const isFinalized = normalizedStatus === 'approved' || normalizedStatus === 'rejected';

  useEffect(() => {
    fetchRegistration();
    fetchClarifications();
  }, [id]);

  const extractDocuments = (reg) => {
    const API_URL = process.env.REACT_APP_BACKEND_URL || '';

    // For company registrations, extract documents from compliance_info
    if (reg.registration_type === 'company' && reg.compliance_info) {
      const complianceInfo = reg.compliance_info;
      const documentFields = [
        { field: 'financial_statement', label: 'Financial Statement' },
        { field: 'registration_certificate', label: 'Registration Certificate' },
        { field: 'iata_fiata_iso', label: 'IATA/FIATA/ISO Certificate' },
        { field: 'business_license', label: 'Business License' },
        { field: 'dg_certification', label: 'DG Certification' },
        { field: 'insurance_certificate', label: 'Insurance Certificate' }
      ];

      const documents = [];
      documentFields.forEach(({ field, label }) => {
        if (complianceInfo[field]) {
          let url = typeof complianceInfo[field] === 'object'
            ? complianceInfo[field].file_url || complianceInfo[field].url
            : complianceInfo[field];
          // Convert relative URLs to absolute URLs
          if (url && url.startsWith('/') && !url.startsWith(API_URL)) {
            url = `${API_URL}${url}`;
          }
          if (url) {
            documents.push({
              name: label,
              field: `doc_${field}`,
              url: url,
              type: 'application/pdf' // Default type, can be enhanced
            });
          }
        }
      });

      // Also check company_info for logo and brochure
      if (reg.company_info) {
        if (reg.company_info.company_logo) {
          let logoUrl = typeof reg.company_info.company_logo === 'object'
            ? reg.company_info.company_logo.file_url || reg.company_info.company_logo.url
            : reg.company_info.company_logo;
          // Convert relative URLs to absolute URLs
          if (logoUrl && logoUrl.startsWith('/') && !logoUrl.startsWith(API_URL)) {
            logoUrl = `${API_URL}${logoUrl}`;
          }
          if (logoUrl) {
            documents.push({
              name: 'Company Logo',
              field: 'doc_company_logo',
              url: logoUrl,
              type: 'image'
            });
          }
        }
        if (reg.company_info.company_brochure) {
          let brochureUrl = typeof reg.company_info.company_brochure === 'object'
            ? reg.company_info.company_brochure.file_url || reg.company_info.company_brochure.url
            : reg.company_info.company_brochure;
          // Convert relative URLs to absolute URLs
          if (brochureUrl && brochureUrl.startsWith('/') && !brochureUrl.startsWith(API_URL)) {
            brochureUrl = `${API_URL}${brochureUrl}`;
          }
          if (brochureUrl) {
            documents.push({
              name: 'Company Brochure',
              field: 'doc_company_brochure',
              url: brochureUrl,
              type: 'application/pdf'
            });
          }
        }
      }

      return documents.length > 0 ? documents : null;
    }

    // For simple registrations, use the documents array
    return reg.documents && reg.documents.length > 0 ? reg.documents : null;
  };

  const fetchRegistration = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/admin/registrations/${id}`);
      const reg = response.data;

      // Extract documents for company registrations
      const documents = extractDocuments(reg);
      if (documents) {
        reg.documents = documents;
      }

      setRegistration(reg);

      // Initialize validation status for all fields
      const initialValidationStatus = {
        company_name: reg.validated_fields?.company_name || 'pending',
        contact_name: reg.validated_fields?.contact_name || 'pending',
        email: reg.validated_fields?.email || 'pending',
        phone: reg.validated_fields?.phone || 'pending',
        country: reg.validated_fields?.country || 'pending',
        city: reg.validated_fields?.city || 'pending',
        address: reg.validated_fields?.address || 'pending',
        services: reg.validated_fields?.services || 'pending',
        documents: reg.validated_fields?.documents || 'pending'
      };

      // Add company registration specific fields
      if (reg.registration_type === 'company') {
        const companyInfo = reg.company_info || {};
        const financialInfo = reg.financial_info || {};
        const complianceInfo = reg.compliance_info || {};
        const branches = reg.branches || [];
        const tradeReferences = reg.trade_references || [];

        // Company info fields - map all fields
        Object.keys(companyInfo).forEach(key => {
          if (!initialValidationStatus[key]) {
            initialValidationStatus[key] = reg.validated_fields?.[key] || 'pending';
          }
        });

        // Ensure required fields for company registrations are initialized
        if (!initialValidationStatus['primary_contact_name']) {
          initialValidationStatus['primary_contact_name'] = reg.validated_fields?.primary_contact_name ||
            reg.validated_fields?.contact_name || 'pending';
        }
        if (!initialValidationStatus['primary_contact_email']) {
          initialValidationStatus['primary_contact_email'] = reg.validated_fields?.primary_contact_email ||
            reg.validated_fields?.email || 'pending';
        }
        if (!initialValidationStatus['primary_contact_mobile']) {
          initialValidationStatus['primary_contact_mobile'] = reg.validated_fields?.primary_contact_mobile ||
            reg.validated_fields?.phone || 'pending';
        }
        if (!initialValidationStatus['country_of_registration']) {
          initialValidationStatus['country_of_registration'] = reg.validated_fields?.country_of_registration ||
            reg.validated_fields?.country || 'pending';
        }

        // Financial info fields
        Object.keys(financialInfo).forEach(key => {
          if (!initialValidationStatus[key]) {
            initialValidationStatus[key] = reg.validated_fields?.[key] || 'pending';
          }
        });

        // Compliance info fields
        Object.keys(complianceInfo).forEach(key => {
          if (!initialValidationStatus[key] && key !== 'financial_statement' && key !== 'registration_certificate' &&
            key !== 'iata_fiata_iso' && key !== 'business_license' && key !== 'dg_certification' &&
            key !== 'insurance_certificate') {
            initialValidationStatus[key] = reg.validated_fields?.[key] || 'pending';
          }
        });

        // Branch fields
        branches.forEach((branch, idx) => {
          Object.keys(branch).forEach(key => {
            const fieldKey = `branch_${idx}_${key}`;
            if (!initialValidationStatus[fieldKey]) {
              initialValidationStatus[fieldKey] = reg.validated_fields?.[fieldKey] || 'pending';
            }
          });
        });

        // Trade reference fields
        tradeReferences.forEach((ref, idx) => {
          Object.keys(ref).forEach(key => {
            const fieldKey = `trade_ref_${idx}_${key}`;
            if (!initialValidationStatus[fieldKey]) {
              initialValidationStatus[fieldKey] = reg.validated_fields?.[fieldKey] || 'pending';
            }
          });
        });
      }

      // Initialize invalid reasons
      const initialInvalidReasons = {
        company_name: reg.validation_reasons?.company_name || '',
        contact_name: reg.validation_reasons?.contact_name || '',
        email: reg.validation_reasons?.email || '',
        phone: reg.validation_reasons?.phone || '',
        country: reg.validation_reasons?.country || '',
        city: reg.validation_reasons?.city || '',
        address: reg.validation_reasons?.address || '',
        services: reg.validation_reasons?.services || '',
        documents: reg.validation_reasons?.documents || ''
      };

      // Add company registration specific invalid reasons
      if (reg.registration_type === 'company' && reg.validation_reasons) {
        Object.keys(reg.validation_reasons).forEach(key => {
          if (!initialInvalidReasons[key]) {
            initialInvalidReasons[key] = reg.validation_reasons[key] || '';
          }
        });
      }

      // Check if there's saved validation status in session storage
      const savedValidation = loadValidationFromSession(id);
      if (savedValidation) {
        // Merge saved validation with initial validation (saved takes precedence)
        const mergedValidationStatus = { ...initialValidationStatus, ...savedValidation.validationStatus };
        const mergedInvalidReasons = { ...initialInvalidReasons, ...savedValidation.invalidReasons };

        setValidationStatus(mergedValidationStatus);
        setInvalidReasons(mergedInvalidReasons);
      } else {
        setValidationStatus(initialValidationStatus);
        setInvalidReasons(initialInvalidReasons);
      }
    } catch (error) {
      toast.error('Failed to load registration details');
      navigate('/admin/registrations');
    } finally {
      setLoading(false);
    }
  };

  const fetchClarifications = async () => {
    try {
      const response = await api.get(`/api/clarifications/admin/${id}`);
      setClarifications(response.data.clarifications || []);
    } catch (error) {
      console.error('Failed to fetch clarifications:', error);
    }
  };

  const openClarification = (clarification) => {
    setSelectedClarification(clarification);
  };

  const saveValidationToSession = (registrationId, validationStatus, invalidReasons) => {
    try {
      const sessionKey = `validation_${registrationId}`;
      sessionStorage.setItem(sessionKey, JSON.stringify({
        validationStatus,
        invalidReasons,
        timestamp: Date.now()
      }));
    } catch (error) {
      console.error('Failed to save validation to session:', error);
    }
  };

  const loadValidationFromSession = (registrationId) => {
    try {
      const sessionKey = `validation_${registrationId}`;
      const saved = sessionStorage.getItem(sessionKey);
      if (saved) {
        const data = JSON.parse(saved);
        // Check if data is less than 24 hours old
        if (Date.now() - data.timestamp < 24 * 60 * 60 * 1000) {
          return data;
        } else {
          // Remove old data
          sessionStorage.removeItem(sessionKey);
        }
      }
    } catch (error) {
      console.error('Failed to load validation from session:', error);
    }
    return null;
  };

  const handleFieldValidation = async (field, status) => {
    const newValidationStatus = { ...validationStatus, [field]: status };
    const newInvalidReasons = { ...invalidReasons };

    // Clear reason if field is no longer invalid
    if (status !== 'invalid') {
      newInvalidReasons[field] = '';
    }

    setValidationStatus(newValidationStatus);
    setInvalidReasons(newInvalidReasons);

    // Save to session storage
    saveValidationToSession(id, newValidationStatus, newInvalidReasons);

    // Optionally save to backend for persistence
    try {
      await api.put(`/api/admin/registrations/${id}/validate-field`, {
        field_path: field,
        status: status,
        notes: status === 'invalid' ? newInvalidReasons[field] : ''
      });
    } catch (error) {
      // Don't show error to user, just log it - session storage is the primary persistence
      console.error('Failed to save validation to backend:', error);
    }
  };

  const handleInvalidReasonChange = (field, reason) => {
    const newInvalidReasons = { ...invalidReasons, [field]: reason };
    setInvalidReasons(newInvalidReasons);

    // Save to session storage when invalid reason changes
    saveValidationToSession(id, validationStatus, newInvalidReasons);
  };

  // Bulk validate/invalidate/reset all fields in a section at once
  const handleValidateSection = async (fieldKeys, status) => {
    const newValidationStatus = { ...validationStatus };
    const newInvalidReasons = { ...invalidReasons };
    const defaultInvalidReason = 'Section marked invalid - please review';

    fieldKeys.forEach((fieldKey) => {
      newValidationStatus[fieldKey] = status;
      if (status === 'invalid') {
        newInvalidReasons[fieldKey] = newInvalidReasons[fieldKey] || defaultInvalidReason;
      } else {
        newInvalidReasons[fieldKey] = '';
      }
    });

    setValidationStatus(newValidationStatus);
    setInvalidReasons(newInvalidReasons);
    saveValidationToSession(id, newValidationStatus, newInvalidReasons);

    try {
      await Promise.all(
        fieldKeys.map((fieldKey) =>
          api.put(`/api/admin/registrations/${id}/validate-field`, {
            field_path: fieldKey,
            status: status,
            notes: status === 'invalid' ? (newInvalidReasons[fieldKey] || defaultInvalidReason) : '',
          })
        )
      );
    } catch (error) {
      console.error('Failed to save section validation to backend:', error);
    }
  };

  // Section header with bulk validation checkbox/buttons
  const renderSectionHeader = (title, fieldKeys, Tag = 'h3') => {
    const allValid = fieldKeys.every((k) => validationStatus[k] === 'valid');
    const allInvalid = fieldKeys.every((k) => validationStatus[k] === 'invalid');
    const TagComponent = Tag;
    const sizeClass = Tag === 'h3' ? 'text-lg' : Tag === 'h5' ? 'text-sm' : 'text-md';

    return (
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <TagComponent className={`${sizeClass} font-medium text-gray-800`}>
          {title}
        </TagComponent>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox
              checked={allValid}
              disabled={isFinalized}
              onCheckedChange={(checked) => {
                handleValidateSection(fieldKeys, checked ? 'valid' : 'pending');
              }}
            />
            <span className="text-green-700 font-medium">Validate All</span>
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox
              checked={allInvalid}
              disabled={isFinalized}
              onCheckedChange={(checked) => {
                handleValidateSection(fieldKeys, checked ? 'invalid' : 'pending');
              }}
            />
            <span className="text-red-700 font-medium">Invalidate All</span>
          </label>
        </div>
      </div>
    );
  };

  const getInvalidFieldsCount = () => {
    return Object.entries(validationStatus).filter(([field, status]) => status === 'invalid').length;
  };

  const getRequiredFieldsValidated = () => {
    if (!registration) return false;

    // For company registrations, check company-specific fields
    if (registration.registration_type === 'company') {
      const requiredFields = [
        'company_name',
        'primary_contact_name', // Contact name for company registrations
        'primary_contact_email', // Email for company registrations
        'primary_contact_mobile', // Phone for company registrations
        'country_of_registration' // Country for company registrations
      ];
      return requiredFields.every(f => validationStatus[f] === 'valid');
    }

    // For simple registrations, use standard fields
    const requiredFields = ['company_name', 'contact_name', 'email', 'phone', 'country'];
    return requiredFields.every(f => validationStatus[f] === 'valid');
  };

  const handleSendForClarification = async () => {
    const invalidFields = Object.entries(validationStatus)
      .filter(([field, status]) => status === 'invalid')
      .map(([field]) => ({
        field,
        reason: invalidReasons[field] || 'Field marked as invalid'
      }));

    if (invalidFields.length === 0) {
      toast.error('No fields marked as invalid');
      return;
    }

    // Check if all invalid fields have reasons
    const missingReasons = invalidFields.filter(f => !f.reason || f.reason.trim() === '');
    if (missingReasons.length > 0) {
      toast.error(`Please provide reasons for all invalid fields: ${missingReasons.map(f => f.field).join(', ')}`);
      return;
    }

    try {
      const fileRequests = requestedFiles
        .filter(rf => rf.label && rf.label.trim())
        .map(rf => rf.label.trim());

      await api.post(`/api/admin/registrations/${id}/clarification`, {
        invalid_fields: invalidFields,
        validation_notes: validationNotes || '',
        requested_files: fileRequests.length > 0 ? fileRequests : undefined
      });

      // Reset invalid fields to pending state after sending clarification
      const updatedValidationStatus = { ...validationStatus };
      const updatedInvalidReasons = { ...invalidReasons };

      invalidFields.forEach(({ field }) => {
        updatedValidationStatus[field] = 'pending';
        updatedInvalidReasons[field] = '';
      });

      setValidationStatus(updatedValidationStatus);
      setInvalidReasons(updatedInvalidReasons);

      // Clear validation notes and file requests after sending
      setValidationNotes('');
      setRequestedFiles([{ label: '' }]);

      // Save updated state to session storage
      saveValidationToSession(id, updatedValidationStatus, updatedInvalidReasons);

      toast.success('Clarification request sent to applicant');
      navigate('/admin/registrations');
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to send clarification request'));
    }
  };

  const handleApproveRegistration = async () => {
    // Check if all required fields are validated
    if (!getRequiredFieldsValidated()) {
      toast.error('Please validate all required fields before approving');
      return;
    }

    try {
      await api.post(`/api/admin/registrations/${id}/approve`, {
        validated_fields: validationStatus,
        validation_notes: '', // Validation notes removed from main page, only shown in clarification view
        send_email: true // Always send email when approving
      });
      toast.success('Registration approved and moved to Members. Email sent to applicant.');
      navigate('/admin/registrations');
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to approve registration'));
    }
  };

  const handleRejectRegistration = async () => {
    // Get rejection reason from invalid reasons or prompt user
    const rejectionReason = Object.values(invalidReasons).find(r => r && r.trim()) || 'Registration rejected';

    if (!rejectionReason || rejectionReason.trim() === '') {
      toast.error('Please mark at least one field as invalid with a reason before rejecting');
      return;
    }

    try {
      await api.post(`/api/admin/registrations/${id}/reject`, {
        reason: rejectionReason
      });
      toast.success('Registration rejected');
      navigate('/admin/registrations');
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to reject registration'));
    }
  };

  const handleOnHoldRegistration = async () => {
    if (!onHoldReason) {
      toast.error('Please provide a reason for putting on hold');
      return;
    }

    try {
      await api.post(`/api/admin/registrations/${id}/on-hold`, {
        reason: onHoldReason,
        additional_info_needed: additionalInfoNeeded || null,
        send_email: true
      });
      toast.success('Registration put on hold. Email sent to applicant.');
      setShowOnHoldDialog(false);
      setOnHoldReason('');
      setAdditionalInfoNeeded('');
      fetchRegistration();
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to put registration on hold'));
    }
  };

  const handleCall = (phone) => {
    window.open(`tel:${phone}`, '_self');
  };

  const handleEmail = (email) => {
    window.open(`mailto:${email}`, '_blank');
  };

  const getValidationBadge = (status) => {
    switch (status) {
      case 'valid':
        return <Badge className="bg-green-100 text-green-700"><CheckCircle size={12} className="mr-1" />Valid</Badge>;
      case 'invalid':
        return <Badge className="bg-red-100 text-red-700"><XCircle size={12} className="mr-1" />Invalid</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-700"><Clock size={12} className="mr-1" />Pending</Badge>;
    }
  };

  // Helper function to render a field with validation
  const renderField = (fieldKey, label, value, isRequired = false) => {
    const displayValue = value || (value === 0 ? '0' : value === false ? 'No' : value === true ? 'Yes' : 'Not provided');
    return (
      <div className="p-4 border rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <Label className="font-medium">{label}{isRequired ? ' *' : ''}</Label>
          {getValidationBadge(validationStatus[fieldKey] || 'pending')}
        </div>
        <p className="text-lg break-words">{displayValue}</p>
        <div className="flex space-x-2 mt-2">
          <Button size="sm" variant={(validationStatus[fieldKey] || 'pending') === 'valid' ? 'default' : 'outline'}
            className={(validationStatus[fieldKey] || 'pending') === 'valid' ? 'bg-green-600' : ''}
            disabled={isFinalized}
            onClick={() => handleFieldValidation(fieldKey, 'valid')}>
            <CheckCircle size={14} className="mr-1" /> Valid
          </Button>
          <Button size="sm" variant={(validationStatus[fieldKey] || 'pending') === 'invalid' ? 'default' : 'outline'}
            className={(validationStatus[fieldKey] || 'pending') === 'invalid' ? 'bg-red-600' : ''}
            disabled={isFinalized}
            onClick={() => handleFieldValidation(fieldKey, 'invalid')}>
            <XCircle size={14} className="mr-1" /> Invalid
          </Button>
        </div>
        {(validationStatus[fieldKey] || 'pending') === 'invalid' && (
          <div className="mt-3">
            <Label className="text-sm text-red-600">Reason for Invalid *</Label>
            <Input
              value={invalidReasons[fieldKey] || ''}
              onChange={(e) => handleInvalidReasonChange(fieldKey, e.target.value)}
              placeholder={`e.g., ${label} is invalid or incomplete`}
              className="mt-1 text-sm"
            />
          </div>
        )}
      </div>
    );
  };

  // Render Company Information Section
  const renderCompanyInformationSection = () => {
    const companyInfo = registration.company_info || {};
    const companyInfoFields = ['company_name', 'business_registration_number', 'trading_name', 'year_established', 'country_of_registration', 'company_website', 'linkedin_page'];
    const headOfficeFields = ['street_address', 'address_line_2', 'city', 'state_region', 'country', 'postal_code'];
    const primaryContactFields = ['primary_contact_name', 'primary_contact_designation', 'primary_contact_email', 'primary_contact_mobile'];
    const secondaryContactFields = ['secondary_contact_name', 'secondary_contact_designation', 'secondary_contact_email', 'secondary_contact_mobile'];
    const companyProfileFields = ['total_employees', 'languages_spoken', 'operating_hours', 'key_ports_airports', 'service_capabilities', 'company_strengths', 'main_trade_lanes', 'markets_to_develop'];

    return (
      <div>
        {renderSectionHeader('Company Information', companyInfoFields, 'h3')}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {renderField('company_name', 'Company Name', companyInfo.company_name || registration.company_name, true)}
          {renderField('business_registration_number', 'Business Registration / License Number', companyInfo.business_registration_number)}
          {renderField('trading_name', 'Trading Name', companyInfo.trading_name)}
          {renderField('year_established', 'Year of Establishment', companyInfo.year_established)}
          {renderField('country_of_registration', 'Country of Registration', companyInfo.country_of_registration || registration.country, true)}
          {renderField('company_website', 'Company Website', companyInfo.company_website)}
          {renderField('linkedin_page', 'LinkedIn Page', companyInfo.linkedin_page)}
        </div>

        {/* Head Office Address */}
        <div className="mt-6">
          {renderSectionHeader('Head Office Address', headOfficeFields, 'h4')}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {renderField('street_address', 'Street Address', companyInfo.street_address, true)}
            {renderField('address_line_2', 'Address Line 2', companyInfo.address_line_2)}
            {renderField('city', 'City', companyInfo.city || registration.city)}
            {renderField('state_region', 'State/Region', companyInfo.state_region)}
            {renderField('country', 'Country', companyInfo.country || registration.country, true)}
            {renderField('postal_code', 'Postal Code', companyInfo.postal_code)}
          </div>
        </div>

        {/* Primary Contact */}
        <div className="mt-6">
          {renderSectionHeader('Primary Contact', primaryContactFields, 'h4')}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {renderField('primary_contact_name', 'Contact Name', companyInfo.primary_contact_name || registration.contact_name, true)}
            {renderField('primary_contact_designation', 'Designation', companyInfo.primary_contact_designation)}
            {renderField('primary_contact_email', 'Email', companyInfo.primary_contact_email || registration.email, true)}
            {renderField('primary_contact_mobile', 'Phone', `${companyInfo.primary_contact_country_code || ''} ${companyInfo.primary_contact_mobile || registration.phone || ''}`.trim(), true)}
          </div>
        </div>

        {/* Secondary Contact */}
        {(companyInfo.secondary_contact_name || companyInfo.secondary_contact_email) && (
          <div className="mt-6">
            {renderSectionHeader('Secondary Contact', secondaryContactFields, 'h4')}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {renderField('secondary_contact_name', 'Contact Name', companyInfo.secondary_contact_name)}
              {renderField('secondary_contact_designation', 'Designation', companyInfo.secondary_contact_designation)}
              {renderField('secondary_contact_email', 'Email', companyInfo.secondary_contact_email)}
              {renderField('secondary_contact_mobile', 'Phone', `${companyInfo.secondary_contact_country_code || ''} ${companyInfo.secondary_contact_mobile || ''}`.trim())}
            </div>
          </div>
        )}

        {/* Company Profile */}
        <div className="mt-6">
          {renderSectionHeader('Company Profile', companyProfileFields, 'h4')}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {renderField('total_employees', 'Total Number of Employees', companyInfo.total_employees, true)}
            {renderField('languages_spoken', 'Languages Spoken', companyInfo.languages_spoken, true)}
            {renderField('operating_hours', 'Operating Days & Hours', companyInfo.operating_hours)}
            {renderField('key_ports_airports', 'Key Airports / Ports Covered', companyInfo.key_ports_airports, true)}
            {renderField('service_capabilities', 'Service Capabilities', companyInfo.service_capabilities, true)}
            {renderField('main_trade_lanes', 'Main Trade Lanes Served', companyInfo.main_trade_lanes, true)}
            {renderField('markets_to_develop', 'Markets to Develop Through Network', companyInfo.markets_to_develop, true)}
          </div>
          <div className="mt-4">
            {renderField('company_strengths', 'Company Strengths / Specialization', companyInfo.company_strengths, true)}
          </div>
        </div>
      </div>
    );
  };

  // Render Branch Offices Section
  const renderBranchOfficesSection = () => {
    const branches = registration.branches || [];
    if (branches.length === 0) return null;

    const allBranchFields = branches.flatMap((_, idx) => [
      `branch_${idx}_name`, `branch_${idx}_address`, `branch_${idx}_city`, `branch_${idx}_country`,
      `branch_${idx}_contact_name`, `branch_${idx}_contact_email`, `branch_${idx}_contact_phone`
    ]);

    return (
      <div>
        {renderSectionHeader('Branch Offices', allBranchFields, 'h3')}
        <div className="space-y-4">
          {branches.map((branch, idx) => {
            const branchFields = [
              `branch_${idx}_name`, `branch_${idx}_address`, `branch_${idx}_city`, `branch_${idx}_country`,
              `branch_${idx}_contact_name`, `branch_${idx}_contact_email`, `branch_${idx}_contact_phone`
            ];
            return (
              <div key={idx} className="p-4 border rounded-lg">
                {renderSectionHeader(`Branch ${idx + 1}`, branchFields, 'h4')}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {renderField(`branch_${idx}_name`, 'Branch Name', branch.branch_name)}
                  {renderField(`branch_${idx}_address`, 'Address', branch.street_address)}
                  {renderField(`branch_${idx}_city`, 'City', branch.city)}
                  {renderField(`branch_${idx}_country`, 'Country', branch.country)}
                  {renderField(`branch_${idx}_contact_name`, 'Contact Name', branch.contact_name)}
                  {renderField(`branch_${idx}_contact_email`, 'Contact Email', branch.contact_email)}
                  {renderField(`branch_${idx}_contact_phone`, 'Contact Phone', `${branch.contact_country_code || ''} ${branch.contact_mobile || ''}`.trim())}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Render Financial Information Section
  const renderFinancialInformationSection = () => {
    const financialInfo = registration.financial_info || {};
    const bankAccountNumber = financialInfo.account_number || financialInfo.bank_account_number;
    const tradeReferences = registration.trade_references || [];

    const mainFinancialFields = financialInfo.history_of_disputes === 'yes'
      ? ['years_in_operation', 'last_fiscal_turnover', 'existing_memberships', 'history_of_disputes', 'dispute_reasons']
      : ['years_in_operation', 'last_fiscal_turnover', 'existing_memberships', 'history_of_disputes'];
    const bankFields = ['bank_name', 'bank_branch_address', 'account_number', 'swift_iban', 'account_holder_name'];
    const allTradeRefFields = tradeReferences.flatMap((_, idx) => [
      `trade_ref_${idx}_company_name`, `trade_ref_${idx}_contact_person`, `trade_ref_${idx}_email`,
      `trade_ref_${idx}_mobile`, `trade_ref_${idx}_relationship_duration`
    ]);

    return (
      <div>
        {renderSectionHeader('Financial Information', mainFinancialFields, 'h3')}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {renderField('years_in_operation', 'Years in Operation', financialInfo.years_in_operation)}
          {renderField('last_fiscal_turnover', 'Last Fiscal Turnover', financialInfo.last_fiscal_turnover ? `${financialInfo.turnover_currency || 'USD'} ${financialInfo.last_fiscal_turnover}` : null)}
          {renderField('existing_memberships', 'Existing Memberships', financialInfo.existing_memberships)}
          {renderField('history_of_disputes', 'History of Disputes', financialInfo.history_of_disputes)}
          {financialInfo.history_of_disputes === 'yes' && renderField('dispute_reasons', 'Dispute Reasons', financialInfo.dispute_reasons)}
        </div>

        {/* Bank Information */}
        <div className="mt-6">
          {renderSectionHeader('Bank Information', bankFields, 'h4')}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {renderField('bank_name', 'Bank Name', financialInfo.bank_name)}
            {renderField('bank_branch_address', 'Bank Branch Address', financialInfo.bank_branch_address)}
            {renderField('account_number', 'Bank Account Number', bankAccountNumber)}
            {renderField('swift_iban', 'SWIFT/IBAN', financialInfo.swift_iban)}
            {renderField('account_holder_name', 'Account Holder Name', financialInfo.account_holder_name)}
          </div>
        </div>

        {/* Trade References */}
        {tradeReferences.length > 0 && (
          <div className="mt-6">
            {renderSectionHeader('Trade References', allTradeRefFields, 'h4')}
            <div className="space-y-4">
              {tradeReferences.map((ref, idx) => {
                const refFields = [
                  `trade_ref_${idx}_company_name`, `trade_ref_${idx}_contact_person`, `trade_ref_${idx}_email`,
                  `trade_ref_${idx}_mobile`, `trade_ref_${idx}_relationship_duration`
                ];
                return (
                  <div key={idx} className="p-4 border rounded-lg">
                    {renderSectionHeader(`Reference ${idx + 1}`, refFields, 'h5')}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {renderField(`trade_ref_${idx}_company_name`, 'Company Name', ref.company_name)}
                      {renderField(`trade_ref_${idx}_contact_person`, 'Contact Person', ref.contact_person)}
                      {renderField(`trade_ref_${idx}_email`, 'Email', ref.email)}
                      {renderField(`trade_ref_${idx}_mobile`, 'Mobile', `${ref.country_code || ''} ${ref.mobile || ''}`.trim())}
                      {renderField(`trade_ref_${idx}_relationship_duration`, 'Relationship Duration', ref.relationship_duration)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render Compliance Section
  const renderComplianceSection = () => {
    const complianceInfo = registration.compliance_info || {};

    const mainComplianceFields = ['lei_number', 'tax_registration_number'];
    const applicantFields = ['applicant_name', 'applicant_designation'];

    return (
      <div>
        {renderSectionHeader('Compliance', mainComplianceFields, 'h3')}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {renderField('lei_number', 'LEI Number', complianceInfo.lei_number)}
          {renderField('tax_registration_number', 'Tax Registration Number', complianceInfo.tax_registration_number)}
        </div>

        {/* Applicant Information */}
        <div className="mt-6">
          {renderSectionHeader('Applicant Information', applicantFields, 'h4')}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {renderField('applicant_name', 'Applicant Name', complianceInfo.applicant_name)}
            {renderField('applicant_designation', 'Applicant Designation', complianceInfo.applicant_designation)}
          </div>
        </div>
      </div>
    );
  };

  // Render Simple Registration Fields
  const renderSimpleRegistrationFields = () => {
    const companyFields = ['company_name', 'contact_name'];
    const contactFields = ['email', 'phone'];
    const locationFields = ['country', 'city', 'address'];

    return (
      <>
        <div>
          {renderSectionHeader('Company Information', companyFields, 'h3')}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {renderField('company_name', 'Company Name', registration.company_name, true)}
            {renderField('contact_name', 'Contact Name', registration.contact_name || registration.full_name, true)}
          </div>
        </div>

        <div>
          {renderSectionHeader('Contact Information', contactFields, 'h3')}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {renderField('email', 'Email', registration.email, true)}
            {renderField('phone', 'Phone', registration.phone, true)}
          </div>
        </div>

        <div>
          {renderSectionHeader('Location Information', locationFields, 'h3')}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {renderField('country', 'Country', registration.country, true)}
            {renderField('city', 'City', registration.city)}
            {renderField('address', 'Address', registration.address)}
          </div>
        </div>

        {registration.services && registration.services.length > 0 && (
          <div>
            {renderSectionHeader('Services', ['services'], 'h3')}
            <div className="p-4 border rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <Label className="font-medium">Services</Label>
                {getValidationBadge(validationStatus.services || 'pending')}
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {registration.services.map((service, idx) => (
                  <Badge key={idx} variant="outline">{service}</Badge>
                ))}
              </div>
              <div className="flex space-x-2 mt-3">
                <Button size="sm" variant={(validationStatus.services || 'pending') === 'valid' ? 'default' : 'outline'}
                  className={(validationStatus.services || 'pending') === 'valid' ? 'bg-green-600' : ''}
                  disabled={isFinalized}
                  onClick={() => handleFieldValidation('services', 'valid')}>
                  <CheckCircle size={14} className="mr-1" /> Valid
                </Button>
                <Button size="sm" variant={(validationStatus.services || 'pending') === 'invalid' ? 'default' : 'outline'}
                  className={(validationStatus.services || 'pending') === 'invalid' ? 'bg-red-600' : ''}
                  disabled={isFinalized}
                  onClick={() => handleFieldValidation('services', 'invalid')}>
                  <XCircle size={14} className="mr-1" /> Invalid
                </Button>
              </div>
              {(validationStatus.services || 'pending') === 'invalid' && (
                <div className="mt-3">
                  <Label className="text-sm text-red-600">Reason for Invalid *</Label>
                  <Input
                    value={invalidReasons.services || ''}
                    onChange={(e) => handleInvalidReasonChange('services', e.target.value)}
                    placeholder="e.g., Services not clearly defined"
                    className="mt-1 text-sm"
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-500">Loading registration details...</p>
        </div>
      </div>
    );
  }

  if (!registration) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Registration not found</p>
        <Button onClick={() => navigate('/admin/registrations')} className="mt-4">
          <ArrowLeft size={16} className="mr-2" />
          Back to Registrations
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="registration-details">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button variant="outline" onClick={() => navigate('/admin/registrations')}>
            <ArrowLeft size={16} className="mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Registration Validation - {registration.company_name}</h1>
            <p className="text-gray-500">Review and validate registration details</p>
          </div>
        </div>
      </div>

      {/* Contact Actions */}
      <Card>
        <CardContent className="p-4">
          <div className="flex space-x-2">
            {registration.phone && (
              <Button size="sm" variant="outline" onClick={() => handleCall(registration.phone)}>
                <Phone size={14} className="mr-2 text-green-600" />
                Call {registration.phone}
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={() => handleEmail(registration.email)}>
              <Mail size={14} className="mr-2 text-blue-600" />
              Email {registration.email}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Documents Section */}
      {registration.documents && registration.documents.length > 0 && (
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Request Documents</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {registration.documents.map((doc, idx) => {
                const fieldKey = doc.field || `doc_${idx}`;
                const status = validationStatus[fieldKey] || 'pending';
                return (
                  <div key={idx} className="p-4 border rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-3 flex-1 min-w-0">
                        {doc.type?.includes('image') ? (
                          <Image size={24} className="text-blue-500 flex-shrink-0" />
                        ) : (
                          <FileText size={24} className="text-gray-500 flex-shrink-0" />
                        )}
                        <span className="text-sm font-medium truncate">{doc.name}</span>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setSelectedDocument(doc);
                          setShowDocumentModal(true);
                        }}
                      >
                        <Eye size={16} />
                      </Button>
                    </div>
                    <div className="flex items-center gap-1 mt-2">
                      {getValidationBadge(status)}
                    </div>
                    <div className="flex space-x-2 mt-2">
                      <Button size="sm" variant={status === 'valid' ? 'default' : 'outline'}
                        className={status === 'valid' ? 'bg-green-600' : ''}
                        disabled={isFinalized}
                        onClick={() => handleFieldValidation(fieldKey, 'valid')}>
                        <CheckCircle size={14} className="mr-1" /> Valid
                      </Button>
                      <Button size="sm" variant={status === 'invalid' ? 'default' : 'outline'}
                        className={status === 'invalid' ? 'bg-red-600' : ''}
                        disabled={isFinalized}
                        onClick={() => handleFieldValidation(fieldKey, 'invalid')}>
                        <XCircle size={14} className="mr-1" /> Invalid
                      </Button>
                    </div>
                    {status === 'invalid' && (
                      <div className="mt-2">
                        <Label className="text-sm text-red-600">Reason *</Label>
                        <Input
                          value={invalidReasons[fieldKey] || ''}
                          onChange={(e) => handleInvalidReasonChange(fieldKey, e.target.value)}
                          placeholder={`e.g., ${doc.name} is unclear or incomplete`}
                          className="mt-1 text-sm"
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Clarifications Section */}
      {clarifications.length > 0 && (
        <Card>
          <CardContent className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Clarifications</h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {clarifications.map((clarification) => (
                    <tr key={clarification.clarification_id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-gray-900">{clarification.clarification_id}</p>
                        <p className="text-xs text-gray-500">{clarification.invalid_fields?.length || 0} field(s)</p>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {clarification.requested_at ? new Date(clarification.requested_at).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="px-4 py-3">
                        {clarification.status === 'pending' && (
                          <Badge className="bg-yellow-100 text-yellow-700">Pending</Badge>
                        )}
                        {clarification.status === 'submitted' && (
                          <Badge className="bg-blue-100 text-blue-700">Submitted</Badge>
                        )}
                        {clarification.status === 'reviewed' && (
                          <Badge className="bg-green-100 text-green-700">Reviewed</Badge>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openClarification(clarification)}
                        >
                          <Eye size={14} className="mr-1" />
                          View
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Document Viewer Modal */}
      {showDocumentModal && selectedDocument && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-6xl max-h-[90vh] w-full flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">{selectedDocument.name}</h3>
              <div className="flex items-center space-x-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => window.open(selectedDocument.url, '_blank')}
                >
                  <ExternalLink size={16} className="mr-2" />
                  Open in New Tab
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowDocumentModal(false);
                    setSelectedDocument(null);
                  }}
                >
                  <X size={20} />
                </Button>
              </div>
            </div>

            {/* Document Content */}
            <div className="flex-1 overflow-auto p-4 flex items-center justify-center bg-gray-100">
              {selectedDocument.type?.includes('image') ? (
                <img
                  src={selectedDocument.url}
                  alt={selectedDocument.name}
                  className="max-w-full max-h-[calc(90vh-120px)] object-contain"
                />
              ) : selectedDocument.url?.toLowerCase().endsWith('.pdf') || selectedDocument.type?.includes('pdf') ? (
                <iframe
                  src={selectedDocument.url}
                  title={selectedDocument.name}
                  className="w-full h-[calc(90vh-120px)] border-0"
                  style={{ minHeight: '600px' }}
                  type="application/pdf"
                />
              ) : (
                <div className="flex flex-col items-center justify-center p-8">
                  <FileText size={64} className="text-gray-400 mb-4" />
                  <p className="text-gray-600 mb-4">Preview not available for this file type</p>
                  <Button
                    variant="outline"
                    onClick={() => window.open(selectedDocument.url, '_blank')}
                  >
                    <ExternalLink size={16} className="mr-2" />
                    Open File
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Clarification Details Modal */}
      {selectedClarification && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">Clarification Details</h2>
                <Button variant="ghost" size="sm" onClick={() => setSelectedClarification(null)}>
                  <X size={20} />
                </Button>
              </div>

              <div className="space-y-4">
                <div>
                  <Label className="text-sm font-medium text-gray-700">Clarification ID</Label>
                  <p className="text-sm text-gray-900">{selectedClarification.clarification_id}</p>
                </div>

                <div>
                  <Label className="text-sm font-medium text-gray-700">Requested At</Label>
                  <p className="text-sm text-gray-900">
                    {selectedClarification.requested_at ? new Date(selectedClarification.requested_at).toLocaleString() : 'N/A'}
                  </p>
                </div>

                {selectedClarification.validation_notes && (
                  <div>
                    <Label className="text-sm font-medium text-gray-700">Validation Notes</Label>
                    <p className="text-sm text-gray-900 mt-1">{selectedClarification.validation_notes}</p>
                  </div>
                )}

                {/* Fields with Admin Reason and User Response */}
                <div>
                  <Label className="text-sm font-medium text-gray-700 mb-3 block">Fields Requiring Clarification</Label>
                  <div className="space-y-4">
                    {selectedClarification.invalid_fields?.map((field, idx) => {
                      const fieldKey = field.field;
                      const isDoc = fieldKey.startsWith('doc_');
                      const userResponse = selectedClarification.response?.field_responses?.[fieldKey];
                      const uploadedFile = isDoc
                        ? (selectedClarification.uploaded_files || []).find(f => f.field === fieldKey)
                        : null;
                      const getFieldLabel = (key) => {
                        const raw = key.startsWith('doc_') ? key.replace(/^doc_/, '') : key;
                        return raw
                          .replace(/_/g, ' ')
                          .replace(/\b\w/g, l => l.toUpperCase())
                          .replace(/company info\./gi, '')
                          .replace(/financial info\./gi, '')
                          .replace(/compliance info\./gi, '')
                          .replace(/branch \d+ /gi, 'Branch ');
                      };

                      return (
                        <div key={idx} className="p-4 border rounded-lg">
                          <div className="mb-3">
                            <p className="font-medium text-sm text-gray-900">{getFieldLabel(fieldKey)}</p>
                            <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded">
                              <p className="text-xs font-medium text-red-700 mb-1">Admin Reason:</p>
                              <p className="text-xs text-red-600">{field.reason}</p>
                            </div>
                          </div>

                          {isDoc && uploadedFile ? (
                            <div className="mt-3 space-y-2">
                              <div className="p-2 bg-green-50 border border-green-200 rounded">
                                <p className="text-xs font-medium text-green-700 mb-1">Updated File:</p>
                                <div className="flex items-center gap-2">
                                  <FileText size={16} className="text-green-600" />
                                  <span className="text-sm text-gray-900">{uploadedFile.original_filename}</span>
                                  {uploadedFile.file_url && (
                                    <Button size="sm" variant="ghost" className="h-6 px-2"
                                      onClick={() => window.open(uploadedFile.file_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${uploadedFile.file_url}` : uploadedFile.file_url, '_blank')}>
                                      <Eye size={14} />
                                    </Button>
                                  )}
                                </div>
                              </div>
                              {userResponse && (
                                <div className="p-2 bg-green-50 border border-green-200 rounded">
                                  <p className="text-xs font-medium text-green-700 mb-1">User Comment:</p>
                                  <p className="text-sm text-gray-900">{userResponse}</p>
                                </div>
                              )}
                            </div>
                          ) : userResponse !== undefined ? (
                            <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded">
                              <p className="text-xs font-medium text-green-700 mb-1">User Response:</p>
                              <p className="text-sm text-gray-900">{userResponse || 'N/A'}</p>
                            </div>
                          ) : (
                            <div className="mt-3 p-2 bg-gray-50 border border-gray-200 rounded">
                              <p className="text-xs text-gray-500">No response yet</p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {selectedClarification.response?.user_description && (
                  <div className="mt-4 p-3 border rounded-lg">
                    <Label className="text-sm font-medium text-gray-700">User Description</Label>
                    <p className="text-sm text-gray-900 mt-1">{selectedClarification.response.user_description}</p>
                  </div>
                )}

                {/* Uploaded Files */}
                {selectedClarification.uploaded_files && selectedClarification.uploaded_files.length > 0 && (
                  <div className="mt-4 p-3 border rounded-lg">
                    <Label className="text-sm font-medium text-gray-700 mb-2 block">
                      <Upload size={14} className="inline mr-1" />
                      Uploaded Files
                    </Label>
                    <div className="space-y-2">
                      {selectedClarification.uploaded_files.map((file, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                          <div className="flex items-center gap-2 min-w-0">
                            <FileText size={16} className="text-gray-500 shrink-0" />
                            <div className="min-w-0">
                              <p className="text-sm font-medium truncate">{file.label}</p>
                              <p className="text-xs text-gray-500 truncate">{file.original_filename}</p>
                            </div>
                          </div>
                          {file.file_url && (
                            <Button size="sm" variant="ghost" onClick={() => window.open(file.file_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${file.file_url}` : file.file_url, '_blank')}>
                              <Eye size={14} />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {selectedClarification.response && (
                  <div className="mt-2">
                    <Label className="text-sm font-medium text-gray-700">Response Submitted At</Label>
                    <p className="text-sm text-gray-900">
                      {selectedClarification.response_submitted_at ? new Date(selectedClarification.response_submitted_at).toLocaleString() : 'N/A'}
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Registration Fields Section */}
      <Card>
        <CardContent className="p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Validate Registration Fields</h2>

          <div className="space-y-6">
            {registration.registration_type === 'company' ? (
              <>
                {/* Company Information */}
                {renderCompanyInformationSection()}

                {/* Branch Offices */}
                {renderBranchOfficesSection()}

                {/* Financial Information */}
                {renderFinancialInformationSection()}

                {/* Compliance */}
                {renderComplianceSection()}
              </>
            ) : (
              <>
                {/* Simple Registration Fields */}
                {renderSimpleRegistrationFields()}
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Validation Notes Section - For Clarifications (Only show when there are invalid fields and record is not finalized) */}
      {!isFinalized && getInvalidFieldsCount() > 0 && (
        <Card>
          <CardContent className="p-6">
            <div className="mb-4">
              <Label className="text-sm font-medium text-gray-700 mb-2 block">Validation Notes (Optional)</Label>
              <Textarea
                value={validationNotes}
                onChange={(e) => setValidationNotes(e.target.value)}
                placeholder="Add optional notes that will be included with the clarification request..."
                rows={4}
                className="w-full"
              />
              <p className="text-xs text-gray-500 mt-2">
                These notes will be included when sending clarifications to the applicant and will appear in the clarification details.
              </p>
            </div>

            {/* Request File Uploads */}
            <div className="mt-6 pt-4 border-t">
              <Label className="text-sm font-medium text-gray-700 mb-2 block">
                <Upload size={14} className="inline mr-1" />
                Request File Uploads (Optional)
              </Label>
              <p className="text-xs text-gray-500 mb-3">
                Ask the applicant to upload additional files along with their clarification response.
              </p>
              <div className="space-y-2">
                {requestedFiles.map((rf, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <Input
                      value={rf.label}
                      onChange={(e) => {
                        const updated = [...requestedFiles];
                        updated[idx] = { ...updated[idx], label: e.target.value };
                        setRequestedFiles(updated);
                      }}
                      placeholder="e.g., Updated Financial Statement, Tax Certificate..."
                      className="flex-1 text-sm"
                    />
                    {requestedFiles.length > 1 && (
                      <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700 px-2"
                        onClick={() => setRequestedFiles(requestedFiles.filter((_, i) => i !== idx))}>
                        <Trash2 size={14} />
                      </Button>
                    )}
                  </div>
                ))}
                <Button size="sm" variant="outline" onClick={() => setRequestedFiles([...requestedFiles, { label: '' }])}>
                  <Plus size={14} className="mr-1" /> Add File Request
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Action Buttons */}
      <Card>
        <CardContent className="p-6">
          <div className="flex justify-between items-center">
            <Button variant="outline" onClick={() => navigate('/admin/registrations')}>
              {isFinalized ? 'Back to List' : 'Cancel'}
            </Button>
            {!isFinalized && (
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  className="text-red-600 border-red-300"
                  onClick={handleRejectRegistration}
                >
                  <XCircle size={16} className="mr-2" />
                  Reject
                </Button>
                <Button
                  variant="outline"
                  className="text-orange-600 border-orange-300"
                  onClick={() => setShowOnHoldDialog(true)}
                >
                  <PauseCircle size={16} className="mr-2" />
                  On Hold
                </Button>
                {getInvalidFieldsCount() > 0 ? (
                  <Button
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                    onClick={handleSendForClarification}
                    disabled={getInvalidFieldsCount() === 0}
                  >
                    <Mail size={16} className="mr-2" />
                    Send for Clarification ({getInvalidFieldsCount()} fields)
                  </Button>
                ) : (
                  <Button
                    className="bg-green-600 hover:bg-green-700 text-white"
                    onClick={handleApproveRegistration}
                    disabled={!getRequiredFieldsValidated()}
                  >
                    <CheckCircle size={16} className="mr-2" />
                    Approve Application
                  </Button>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* On Hold Dialog */}
      <Dialog open={showOnHoldDialog && !isFinalized} onOpenChange={setShowOnHoldDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Put Registration On Hold</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              The applicant will receive an email with the reason and any additional information you request.
            </p>

            <div>
              <Label>Reason for Hold *</Label>
              <Textarea
                value={onHoldReason}
                onChange={(e) => setOnHoldReason(e.target.value)}
                placeholder="e.g., Documents need verification, Missing company registration..."
                rows={3}
              />
            </div>

            <div>
              <Label>Additional Information Needed (Optional)</Label>
              <Textarea
                value={additionalInfoNeeded}
                onChange={(e) => setAdditionalInfoNeeded(e.target.value)}
                placeholder="e.g., Please provide:&#10;- Business license copy&#10;- Company registration certificate"
                rows={4}
              />
            </div>

            <div className="flex justify-end space-x-2 pt-2">
              <Button variant="outline" onClick={() => setShowOnHoldDialog(false)}>
                Cancel
              </Button>
              <Button
                className="bg-orange-600 hover:bg-orange-700 text-white"
                onClick={handleOnHoldRegistration}
              >
                <PauseCircle size={16} className="mr-2" />
                Put On Hold & Send Email
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RegistrationDetails;
